/**
 * WHAT THE APP STORES, declared once.
 *
 * A new synced collection used to mean editing `firestore.rules` and
 * `pb/pb_migrations/` - two files, in two rule languages, that can drift while
 * everything still compiles and most tests still pass. This is the one place
 * that says what exists, and `tools/gen-schema.mjs` writes both.
 *
 * IT DOES NOT DESCRIBE EVERY COLLECTION, on purpose. Four of them -
 * `joinCodes`, `sessions`, `members`/the members map, and `players` - have
 * rules that are genuinely bespoke: a capability token that may be read but
 * never listed, a create that must self-register the caller as GM, an update
 * that a non-GM may make only to one field. Generating those would mean
 * inventing a vocabulary rich enough to express each one exactly once, which
 * is a worse thing to maintain than four hand-written blocks. They stay
 * hand-written, and this file says so rather than pretending otherwise.
 *
 * What IS here is the repetitive nine, which is where drift actually happens:
 * the session-scoped payload collections, all variations on two rules.
 *
 * @see tools/gen-schema.mjs - turns this into rules and migrations
 * @see src/test/architecture/schemaSync.test.ts - fails if the outputs drift
 */

/**
 * Who may touch a collection.
 *
 * Deliberately a short list. Every value here has to be expressible in BOTH
 * rule languages, and an access model with fifteen cases is one nobody can
 * hold in their head while reviewing a security change.
 */
type Access =
  /** Anyone in the session. Most of the app. */
  | 'member'
  /** The GM alone. Enforced fog of war - a client-side secret is readable from a console. */
  | 'gm'
  /**
   * The GM, or a member the `waitingRoom` lock is open to: their own override,
   * else the table-wide lock. Only `gmWaiting` uses it - the rule names that lock.
   */
  | 'gm-or-grant'
  /** Read by every member; written by the GM alone (chat groups). */
  | 'gm-writes'
  /**
   * AUDIENCE-SCOPED, posted by members (chat, v8.363.0). A record carries
   * `audience: uids` - `'*'` for everyone, `'@players'` for every non-GM
   * member, or explicit uids - and is READ only by those it names; the GM
   * is not an override, which is what makes a player-to-player whisper
   * private by rule. Created by a member as themself (`fromUid`), updated
   * by its author, deleted by its author or the GM.
   */
  | 'audience-post'
  /** Audience-scoped, GM-written (handouts): the GM reads all, a member what names them. */
  | 'gm-audience'
  /**
   * ONE'S OWN (v9.8.0, the handouts' seen marks). ONE record a person, whose id
   * is their uid and which carries `uid: text`, its owner: read by every member,
   * created by a member as themself under their own id, updated
   * only by its owner (who cannot hand it to someone else), deleted by its owner
   * or the GM. What a person may say about themself and nobody may say for them.
   */
  | 'member-own'
  /**
   * ONE'S OWN, READ BY ITS OWNER AND THE GM (2026-10-31, plan sync-listeners: a seat's diagnostics). As
   * `member-own` - one record a person under their own id, carrying `uid`, written by its owner alone - but READ
   * only by its owner and the GM: what a browser saw is for the GM's bug report, not for the other players.
   */
  | 'own-gm-reads'
  /**
   * AN ACCOUNT'S OWN (the asset library, 2026-10-18). Lives under
   * `owners/{uid}/…`, outside every session: read and written by that account
   * alone, and only if it can host - a Firebase account that is not
   * anonymous, a PocketBase user that is not a guest. The path proves the
   * owner, so no query filter is needed.
   */
  | 'owner';

/**
 * A column's type. Beyond the four scalars (v8.363.0): `uids` is a list of
 * people - an array in Firestore, a json column in PocketBase that the rules
 * match with LIKE over its text (`audience ~ '"<uid>"'`, measured on 0.39.10:
 * `?=` does not reach into a json array); `{ type: 'text', max }` caps a
 * text column; `file` is a native PocketBase file field / a Storage object
 * path in Firestore, written by the files port, never by setDoc.
 */
export type FieldType =
  | 'text'
  | 'number'
  | 'bool'
  | 'json'
  | 'uids'
  | { type: 'text'; max: number }
  | { type: 'file'; maxSize: number; mimeTypes: string[]; protected?: boolean };

/** The scalar name of a field type - what the reconciler and the json rule reason about. */
export function fieldKind(t: FieldType): 'text' | 'number' | 'bool' | 'json' | 'uids' | 'file' {
  return typeof t === 'string' ? t : t.type;
}

export interface CollectionSpec {
  /** The last path segment: `sessions/{id}/combatants/{cid}` → `combatants`. */
  name: string;
  access: Access;
  /**
   * PocketBase needs typed columns; Firestore does not care.
   *
   * `text` for anything the app stringifies - a `json` column round-trips a
   * payload with its keys REORDERED, and echo prevention compares those
   * strings raw (decisions-log 2026-08-11). `json` is only ever right for a
   * field rebuilt through a factory on read, which is why `combatants.state`
   * is the sole exception in the app.
   */
  fields: Record<string, FieldType>;
  /** One line, for the humans reading the generated files. */
  note?: string;
  /**
   * For an owner collection nested under another's document: the parent
   * collection's name (`owners/{uid}/assets/{assetId}/assetVersions/{v}` has
   * parent `assets`). Firestore nests the match; PocketBase is flat anyway.
   */
  parent?: string;
}

/**
 * A handout file's ceiling on both servers: the PocketBase column's maxSize
 * and, through the generator, the Firestore document's `size` cap. The
 * engine's MAX_FILE_BYTES and the hand-written storage.rules carry the same
 * number - schemaSync.test.ts holds all of them to this one. (This file
 * imports nothing: the generator loads it under plain Node.)
 */
export const HANDOUT_FILE_BYTES = 50 * 1024 * 1024;

/** Every session-scoped collection whose rules are one of the three patterns. */
export const SESSION_COLLECTIONS: CollectionSpec[] = [
  {
    name: 'combatants',
    access: 'member',
    note: 'The roster. `state` is the hot half (350 ms), the rest is cold (700 ms).',
    fields: {
      name: 'text',
      kind: 'text',
      order: 'number',
      sheet: 'text',
      overrides: 'text',
      loadoutRef: 'text',
      slotOrder: 'text',
      // The COLD clock (v8.273.0), and it must be declared or the whole
      // stale-echo veto inverts: PocketBase silently drops an undeclared
      // field, so every remote sheet reads back rev 0, and the moment a
      // client's own clock passes 0 the `remoteSheetRev >= localSheetRev`
      // gate refuses every remote sheet FOREVER. Other players stop seeing
      // each other's character edits, which is precisely what it was added
      // to prevent (missing until v8.309.3 - the first self-hosted playtest
      // found it).
      sheetRev: 'number',
      // The one json column in the app: combatantFromDoc rebuilds it through
      // emptyCombatState, so key order comes from the factory, not the wire.
      state: 'json',
      schemaVersion: 'number',
      updatedAt: 'number',
      updatedBy: 'text',
    },
  },
  {
    name: 'narrative',
    access: 'member',
    note: 'Append-only combat log. Ids are deterministic, so a retry overwrites.',
    fields: {
      type: 'text',
      text: 'text',
      targetName: 'text',
      timestamp: 'number',
      userName: 'text',
      attackGroupId: 'text',
      // Which area attack a line belongs to, so the log can draw one blast as
      // one block. Declared the same day `volleyId` was dropped from the
      // defence collection for want of a line here (v8.155.0).
      volleyId: 'text',
      volleySize: 'number',
      slotName: 'text',
      structured: 'text',
      by: 'text',
      // What kind of line its author says it is (v9.7.0, engine/log/lanes): the story's lines say `story`.
      lane: 'text',
      // An attack's beat facts - who struck, melee, landed/miss/jam (engine/attackBeat, plan map-display D).
      beat: 'text',
    },
  },
  {
    name: 'defence',
    access: 'member',
    note: 'One pending prompt per tab. First resolver wins - see pb_hooks/cas.pb.js.',
    // THE WHOLE ATTACK, not a reference to one. `useAttackExecution` writes
    // every field the defender's client needs to render the prompt and roll
    // against it, because the defender may be a different person on a
    // different machine who has none of that state. So this list must track
    // that payload exactly: a field written here and not declared is DROPPED
    // by PocketBase, silently. That is how `hits` went missing and killed
    // CombatLogPanel on the defender's screen with no error anywhere near it.
    // The adapter now throws on a dropped field, so the failure is loud - but
    // keeping this list correct is what stops it happening at all.
    fields: {
      data: 'text',
      // The roll and its arithmetic.
      attackRoll: 'number',
      modifiedSkill: 'number',
      dos: 'number',
      dof: 'number',
      totalHits: 'number',
      overheatDmg: 'number',
      isMelee: 'bool',
      furAssaultEligible: 'bool',
      effectiveMode: 'text',
      tabId: 'text',
      // The attacker's team tag (v8.368.4) and reaction exposures (v8.368.3):
      // null-coerced by the writer, read by the defender's chain and banner.
      attackerTeamTag: 'text',
      // The attacker's side (2026-10-23): allied teams share one. An older
      // payload has none and the reader falls back to the tag.
      attackerSideTag: 'text',
      attackerReactionMods: 'json',
      // The attacker's exposure codes (v8.368.5): the shooter's `pin` reaches
      // the pinning test the defender's client rolls.
      attackerExposures: 'json',
      attackerAuras: 'json',
      areaRolled: 'json',
      // Nested payloads. `json` is right here and `text` is not: the app
      // writes these as OBJECTS, never as strings it later compares raw, so
      // there is no byte-identity to preserve. The defence listener sets
      // state and never writes back, so a re-encode cannot start a storm.
      // Written by every attack since v8.246.0 (Haywire) and v8.155.0 (the
      // volley's size) and declared only now: PocketBase dropped them silently,
      // and the console said so on every shot (v8.310.14).
      haywireRoll: 'number',
      volleySize: 'number',
      hits: 'json',
      slot: 'json',
      specialNotes: 'json',
      cdResult: 'json',
      skillBreakdown: 'json',
      frozenTarget: 'json',
      // The attack's kind and rate, for the defender's beat (plan design-gaps K1). Written since v11.36.0 and declared
      // only on 2026-10-15: PocketBase dropped it on every defended attack (useAttackExecution.test, WRITES ONLY).
      beatKind: 'json',
      // Who asked, and who answered.
      createdAt: 'number',
      resolvedBy: 'text',
      resolvedAt: 'number',
      choice: 'text',
      attackerId: 'text',
      attackerName: 'text',
      // The acting COMBATANT (attackerId is the account), for the display's beat (plan map-display, D).
      actorId: 'text',
      // Ties the victims of ONE area attack together. Null for an ordinary
      // attack, which is why Firebase never noticed it was undeclared here -
      // the field only appears once a blast catches more than one combatant,
      // and no Firebase E2E fires a blast at a populated map.
      volleyId: 'text',
    },
  },
  {
    name: 'characters',
    access: 'member',
    note: 'The shared character library. One of the three things a migration moves.',
    fields: { data: 'text', name: 'text', updatedAt: 'number', updatedBy: 'text', ownerUid: 'text' },
  },
  {
    name: 'afflictionPresets',
    access: 'member',
    note: "The table's effect palette (v8.199.2; any kind since v8.370.1 - `kind` absent reads as affliction). `spec` is a stringified spec - text, never json. The collection keeps its first name.",
    fields: { name: 'text', kind: 'text', spec: 'text', updatedAt: 'number', updatedBy: 'text' },
  },
  {
    name: 'loadouts',
    access: 'member',
    note: 'Shared weapon kits. Also migrated.',
    fields: { data: 'text', name: 'text', updatedAt: 'number', updatedBy: 'text' },
  },
  {
    name: 'battlefield',
    access: 'member',
    note: 'Tokens AND pings in one collection, told apart by `kind`.',
    // Two record shapes share this collection, and BOTH write their fields at
    // the top level rather than inside a blob - `setBattlefieldToken` and
    // `setBattlefieldPing` in multiplayer.ts declare them exactly. The union
    // of the two is what has to be declared here; the six token geometry
    // fields were missing, so every drag wrote a token with no position.
    fields: {
      kind: 'text',
      data: 'text',
      rev: 'number',
      // Token geometry.
      x: 'number',
      z: 'number',
      elevation: 'number',
      eyeHeight: 'number',
      footprint: 'number',
      height: 'number',
      // json, NOT number, and this is the one field where that matters.
      // A token with no heading must be distinguishable from one pointing
      // north, and a `number` column has no way to say "absent" - it reads
      // back 0, which is due north. So the arrow was drawn as SET on every
      // fresh vehicle, pointing north, and the "unset" state could never be
      // reached. A json column stores null and 0 as different things.
      heading: 'json',
      ignoresTerrain: 'bool',
      // Ping. `y` and `type` are its own; `x`/`z` it shares with a token.
      type: 'text',
      y: 'number',
      by: 'text',
      byName: 'text',
      at: 'number',
      note: 'text',
    },
  },
  {
    name: 'shared',
    access: 'member',
    note: "Nine singleton documents (`cohesion` since Squad Mode, 2026-10-23: the kill-teams' pools, member-writable, every change a compare-and-set). `permissions` is GM-write-only on both backends - see the generator.",
    fields: {
      doc: 'text',
      data: 'text',
      locks: 'json',
      playerOverrides: 'json',
      houseRules: 'json',
    },
  },
  {
    name: 'battlefieldGm',
    access: 'gm',
    note: 'Hidden terrain. Enforced, not hidden client-side - a console reads that.',
    fields: { data: 'text', updatedAt: 'number' },
  },
  {
    name: 'mapLibrary',
    access: 'member',
    note: "A saved map per document (v8.310.19). One JSON blob for the whole library hit Firestore's 1 MiB ceiling at 10 MB, and the refused write jammed every write behind it.",
    // `parts` and `part` since v8.334.1: written from v8.310.20, never declared, so PocketBase
    // dropped them and every saved map read as torn there. See gotchas.
    fields: { name: 'text', parts: 'number', part: 'number', data: 'text', updatedAt: 'number', updatedBy: 'text' },
  },
  {
    name: 'gmWaiting',
    access: 'gm-or-grant',
    note: 'Undeployed ambushes. Same reason - open to a player by the waitingRoom lock: a table-wide unlock or their own grant.',
    fields: { data: 'text', updatedAt: 'number' },
  },
  {
    name: 'chat',
    access: 'audience-post',
    note: 'The vox (v8.363.0): a line from a person to an audience. `speakAs` and `roll` are stringified - text, never json.',
    fields: {
      ts: 'number',
      fromUid: 'text',
      fromSeat: 'text',
      fromName: 'text',
      speakAs: 'text',
      kind: 'text',
      text: { type: 'text', max: 4000 },
      conv: 'text',
      audience: 'uids',
      mentions: 'uids',
      roll: 'text',
      handoutId: 'text',
    },
  },
  {
    name: 'chatChannels',
    access: 'gm-writes',
    note: 'GM-defined vox groups (v8.363.0): a named list of people, whether the GM listens, and the team it was seeded from.',
    fields: {
      name: 'text',
      members: 'uids',
      gmListens: 'bool',
      createdBy: 'text',
      createdAt: 'number',
      fromTeam: 'text',
    },
  },
  {
    name: 'asks',
    access: 'member',
    note: 'One seat asking another for an answer (2026-10-05): who an area attack catches when positions do not count, and later the calls for tests. One document per ask; every transition is a compare-and-set on `data` (`engine/asks.ts`), and the asker deletes it once read. Member-writable because both ends write it; nothing secret may ride in one.',
    fields: { data: 'text' },
  },
  {
    name: 'direction',
    access: 'gm-writes',
    note: "What the GM directs (2026-10-04): `frame` holds the session phase (narrative, stealth, combat); `cut` the GM's cut, every display camera to one figure (plan map-display, C). Read by every member, written by the GM alone. Not `shared/permissions`: that is a full-replace document a settings edit would race.",
    fields: { data: 'text' },
  },
  {
    name: 'clocks',
    access: 'gm-audience',
    note: "Clocks (2026-10-05): a named count of segments the GM fills. One document per clock; `audience` is `[]` while the GM keeps it to themself and `['*']` once the table may watch it.",
    fields: { data: 'text', audience: 'uids' },
  },
  {
    name: 'board',
    access: 'gm-audience',
    note: 'The board (2026-10-05): what the table knows - a lead, a name, a pinned line of the story. One document per entry, hidden or shown by `audience` like a clock.',
    fields: { data: 'text', audience: 'uids' },
  },
  {
    name: 'testCalls',
    access: 'gm',
    note: 'A call for a test while it is open (v9.5.3): the call, who is still to answer, the results so far. GM-only so a secret result may sit in it; deleted when the call closes. One document per call.',
    fields: { data: 'text', updatedAt: 'number' },
  },
  {
    name: 'gmHidden',
    access: 'gm',
    note: "A hidden figure (plan map-display, G1): one per document, keyed by the combatant - its combatant, token, alias and initiative, as text. GM only: never on the roster, never on a player's machine, not even to a waiting-room grantee.",
    fields: { data: 'text', updatedAt: 'number' },
  },
  {
    name: 'gmDesk',
    access: 'gm',
    note: "The GM's desk (2026-10-05): notes and the hidden half of anything a player sees only part of. Enforced by rule - a client-side secret is readable from a console.",
    fields: { data: 'text' },
  },
  {
    name: 'handouts',
    access: 'gm-audience',
    note: 'Handouts (v8.363.9): an image, a PDF, a note or a link the GM shows to an audience. The files ride the files port, never setDoc.',
    fields: {
      kind: 'text',
      title: 'text',
      folder: 'text',
      tags: 'text',
      audience: 'uids',
      notes: { type: 'text', max: 20000 },
      url: 'text',
      file: {
        type: 'file',
        maxSize: HANDOUT_FILE_BYTES,
        mimeTypes: ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'],
        protected: true,
      },
      thumb: { type: 'file', maxSize: 1048576, mimeTypes: ['image/jpeg', 'image/webp', 'image/png'], protected: true },
      size: 'number',
      width: 'number',
      height: 'number',
      pages: 'number',
      createdBy: 'text',
      createdAt: 'number',
      updatedAt: 'number',
      revealedAt: 'number',
      // When the GM put it up on every screen in its audience (v9.8.0); 0 or absent = not presented.
      presentedAt: 'number',
      // The library asset and version it was copied from (v11.82.0), as JSON; '' for one made at the table.
      origin: 'text',
    },
  },
  {
    name: 'handoutSeen',
    access: 'member-own',
    note: "Which handouts a person has opened (v9.8.0): ONE document per person, a stringified map handoutId -> seenAt, pruned to the session's handouts on every write so it stays bounded. The GM reads them all for 'seen by 2 of 3'.",
    fields: { uid: 'text', data: 'text', updatedAt: 'number' },
  },
  {
    name: 'seatDiag',
    access: 'own-gm-reads',
    note: "A browser's last diagnostics lines (2026-10-31, plan sync-listeners): ONE document per person, uploaded by their own seat, read by the GM's bug report when it is copied. Off the seat record so a heartbeat no longer delivers up to 80 lines per seat to every screen.",
    fields: { uid: 'text', diag: { type: 'text', max: 200000 }, diagAt: 'number' },
  },
  {
    name: 'scenes',
    access: 'gm',
    note: 'A scene in the wings (v8.334.0): its record - name, marks, tracker, marker. Small; the cast and the terrain are their own documents.',
    fields: { data: 'text', name: 'text', order: 'number', updatedAt: 'number' },
  },
  {
    name: 'sceneCast',
    access: 'gm',
    note: 'One parked combatant per document, `<sceneId>--<combatantId>` - a cast of thirty in one blob is the room-of-thirty ceiling (gotcha 12).',
    fields: { scene: 'text', data: 'text', updatedAt: 'number' },
  },
  {
    name: 'sceneTerrain',
    access: 'gm',
    note: "A scene's terrain, packed and split into parts like the map library. GM-only: hidden terrain stays hidden, and the next map is not a spoiler.",
    fields: { name: 'text', parts: 'number', part: 'number', data: 'text', updatedAt: 'number' },
  },
];

/** The longest thumbnail a library card stores, as a data URL (a 264x156 WebP is about 15 kB). */
export const ASSET_THUMB_CHARS = 60_000;

/**
 * THE ASSET LIBRARY (2026-10-18): one per hosting account, under
 * `owners/{uid}/…`. The rows the page watches are small - folders and one row
 * an asset; what an asset holds is in its versions, parts, thumb and files,
 * nested under it and read only on demand, never watched.
 */
export const OWNER_COLLECTIONS: CollectionSpec[] = [
  {
    name: 'assetFolders',
    access: 'owner',
    note: 'A folder in the library: one row, so renaming or moving it is one write and nothing below it changes.',
    fields: { name: 'text', parentId: 'text', createdAt: 'number', updatedAt: 'number' },
  },
  {
    name: 'assets',
    access: 'owner',
    note: 'One asset: what the cards show. nextVersion is reserved before a save writes; version is the last one whole.',
    fields: {
      kind: 'text',
      name: 'text',
      folderId: 'text',
      tags: 'text',
      nextVersion: 'number',
      version: 'number',
      size: 'number',
      bytes: 'number',
      thumbVersion: 'number',
      lastNote: 'text',
      createdAt: 'number',
      updatedAt: 'number',
      deletedAt: 'number',
      trashedFrom: 'text',
    },
  },
  {
    name: 'assetVersions',
    access: 'owner',
    parent: 'assets',
    note: "One saved version's facts, small so the panel can list them all. Written AFTER its parts, so a version without one is a save that never finished.",
    fields: { version: 'number', parts: 'number', size: 'number', note: 'text', at: 'number', by: 'text' },
  },
  {
    name: 'assetParts',
    access: 'owner',
    parent: 'assets',
    note: "A version's content, in parts under Firestore's 1 MiB a document (engine/storage/parts): `v0007-p0`, `v0007-p1`…",
    fields: { version: 'number', parts: 'number', part: 'number', data: 'text', at: 'number' },
  },
  {
    name: 'assetThumbs',
    access: 'owner',
    parent: 'assets',
    note: "The card's picture, rendered on save: one document, read when the card is on screen.",
    fields: { version: 'number', data: { type: 'text', max: ASSET_THUMB_CHARS } },
  },
  {
    name: 'assetFiles',
    access: 'owner',
    parent: 'assets',
    note: "An image's or handout's bytes for one version, through the files port. Firebase needs Storage turned on.",
    fields: {
      version: 'number',
      contentType: 'text',
      size: 'number',
      file: {
        type: 'file',
        // A handout's ceiling: a handout is what an asset file becomes in a session.
        maxSize: HANDOUT_FILE_BYTES,
        mimeTypes: ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'],
        protected: true,
      },
    },
  },
];

/**
 * The four whose rules are hand-written, and why.
 *
 * Listed so the set is complete somewhere. `schemaSync.test.ts` checks that
 * every collection the app actually writes to appears in one list or the
 * other - a new one silently missing from both is the failure this whole file
 * exists to prevent.
 */
export const HAND_WRITTEN: { name: string; why: string }[] = [
  { name: 'joinCodes', why: 'a capability token: readable by code, never listable, never editable' },
  { name: 'sessions', why: 'create must self-register the caller as GM and sole member' },
  { name: 'members', why: 'PocketBase only - Firestore keeps a members MAP on the session' },
  { name: 'players', why: 'create and update are scoped to your own seat id' },
];

/** Every collection the app stores anything in. */
export const ALL_COLLECTIONS = [
  ...SESSION_COLLECTIONS.map((c) => c.name),
  ...OWNER_COLLECTIONS.map((c) => c.name),
  ...HAND_WRITTEN.map((h) => h.name),
];
